import * as React from "react";
import Alert, { type AlertVariantKey } from "@imminently/decisively-ui/Alert";
import Icon, { type IconName } from "@imminently/decisively-ui/Icon";
import Select from "@imminently/decisively-ui/Select";
import TextInput from "@imminently/decisively-ui/TextInput";
import {
  ComponentPage,
  ComponentPreview,
  ComponentSection,
  ControlPanel,
  ControlRow,
} from "../components/ComponentPage";

const variantIcon: Partial<Record<AlertVariantKey, IconName>> = {
  info: "info",
  success: "success",
  warning: "warning",
  destructive: "error",
};

const variants: AlertVariantKey[] = ["info", "success", "warning", "destructive", "subtle", "secondary"];

export function AlertPage() {
  const [variant, setVariant] = React.useState<AlertVariantKey>("info");
  const [title, setTitle] = React.useState("Heads up");
  const [description, setDescription] = React.useState("This is a description that gives more detail about the alert.");

  return (
    <ComponentPage
      title="Alert"
      description="Displays a short, important message in a way that attracts the user's attention without interrupting their task. Uses the same variant names as Chip but its own outlined styling."
    >
      <ComponentSection title="Playground" description="Adjust the variant, title, and description.">
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview>
            <Alert
              variant={variant}
              icon={variantIcon[variant] ? <Icon name={variantIcon[variant]} /> : undefined}
              title={title}
              description={description}
              className="w-full max-w-md"
            />
          </ComponentPreview>
          <ControlPanel>
            <ControlRow label="Variant">
              <Select
                value={variant}
                onValueChange={(next) => setVariant((next ?? "info") as AlertVariantKey)}
                options={variants.map((v) => ({ value: v, label: v }))}
              />
            </ControlRow>
            <ControlRow label="Title">
              <TextInput
                value={title}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTitle(event.target.value)}
              />
            </ControlRow>
            <ControlRow label="Description">
              <TextInput
                value={description}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDescription(event.target.value)}
              />
            </ControlRow>
          </ControlPanel>
        </div>
      </ComponentSection>

      <ComponentSection title="Variants" description="info matches the outlined box from CreateMappingDialog (Tailwind blue-50/200/950); the rest use the shared *-light theme tokens.">
        <ComponentPreview className="flex-col items-stretch gap-4">
          {variants.map((v) => (
            <Alert
              key={v}
              variant={v}
              icon={variantIcon[v] ? <Icon name={variantIcon[v]} /> : undefined}
              title={v}
              description={`This is a ${v} alert.`}
            />
          ))}
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection title="Description only" description="No title — just a single message, e.g. an inline notice on a page.">
        <ComponentPreview className="flex-col items-stretch gap-4">
          <Alert variant="info" icon={<Icon name="info" />}>
            This information comes from the auth provider and may take some time to update.
          </Alert>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection title="Composed" description="Use the subcomponents directly for full control over layout.">
        <ComponentPreview className="flex-col items-stretch gap-4">
          <Alert variant="destructive">
            <Alert.Icon>
              <Icon name="error" />
            </Alert.Icon>
            <Alert.Content>
              <Alert.Title>Failed to delete tenancy</Alert.Title>
              <Alert.Description>
                Error deleting group: Resource '4c96ff15-62cf-448b-a128-c8fa8339c196' does not exist or one of its queried reference-property objects are not present.
              </Alert.Description>
            </Alert.Content>
          </Alert>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection title="Without an icon" description="icon is optional — omit it for a plain color banner.">
        <ComponentPreview className="flex-col items-stretch gap-4">
          <Alert variant="warning" title="No icon" description="Alerts render fine without an icon prop." />
        </ComponentPreview>
      </ComponentSection>
    </ComponentPage>
  );
}
